<template>
  <v-container v-if="$can('browse', 'YachtManufacturer')">
    <!--<editor-fold desc="Page header">-->
    <v-row

      align="center"
      class="mb-12"
    >
      <v-col>
        <div class="headline">
          Yacht Manufacturers
        </div>
      </v-col>
      <v-col class="shrink">
        <v-btn
          v-if="$can('create', 'YachtManufacturer')"
          color="primary"
          class="ma-0"
          @click="openYachtManufacturersEditDialog()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>
    <!--</editor-fold>-->

    <v-row>
      <v-col
        cols="3"
      >
        <v-subheader class="justify-space-between pr-0">
          Filters
          <v-btn
            v-if="isFiltered"
            text
            color="red"
            @click="clearFilters"
          >
            <v-icon
              small
              left
            >
              close
            </v-icon>
            Clear
          </v-btn>
        </v-subheader>
        <v-text-field
          v-model="filtersDirty.name"
          solo
          label="Name"
          hide-details
          clearable
          class="mb-2"
          autocomplete="no"
        />
        <v-autocomplete
          v-model="filtersDirty.country_id"
          solo
          label="Country"
          :items="countries.data"
          :loading="countries.loading"
          item-text="name"
          item-value="id"
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="13">
        <!--<editor-fold desc="Pagination header">-->
        <pagination
          :from="yachtManufacturers.meta.from"
          :to="yachtManufacturers.meta.to"
          :total="yachtManufacturers.meta.total"
          :last-page="yachtManufacturers.meta.last_page"
          :page.sync="pagination.page"
          class="mb-1 align-end"
        />
        <!--</editor-fold>-->
        <!--<editor-fold desc="Table">-->
        <v-data-table
          :headers="headers"
          :items="yachtManufacturers.data"
          :loading="yachtManufacturers.loading"
          :page.sync="pagination.page"
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.desc"
          :server-items-length="yachtManufacturers.meta.total"
          hide-default-footer
          class="elevation-5"
        >
          <template v-slot:item="{ item }">
            <yacht-manufacturer-row
              :yacht-manufacturer="item"
              @click.native="openYachtManufacturersEditDialog(item)"
            />
          </template>
          <template slot="no-data">
            <!--<editor-fold desc="Filtered">-->
            <v-row
              v-if="isFiltered"

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no results for your search..
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  class="ma-0"
                  small
                  text
                  @click="clearFilters()"
                >
                  <v-icon left>
                    clear
                  </v-icon>
                  Clear Filters
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
            <!--<editor-fold desc="NoData">-->
            <v-row
              v-else

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no yacht manufacturers at the moment.
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  v-if="$can('create', 'YachtManufacturer')"
                  color="primary"
                  class="ma-0"
                  small
                  @click="openYachtManufacturersEditDialog()"
                >
                  <v-icon left>
                    add
                  </v-icon>
                  Add New
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
          </template>
          <table-footer
            slot="footer"
            :last-page="yachtManufacturers.meta.last_page"
            :page.sync="pagination.page"
          />
        </v-data-table>
        <!--</editor-fold>-->
      </v-col>
    </v-row>

    <yacht-manufacturer-edit-dialog
      :visible.sync="yachtManufacturerEditDialog"
      :yacht-manufacturer="yachtManufacturerDirty"
      @update:yachtManufacturer="fetchYachtManufacturers"
    />
  </v-container>
</template>

<script>
  import { cloneDeep } from 'lodash'
  import { mapGetters } from 'vuex'
  import paginationMixin from '@/mixins/paginationMixin'
  import filterableMixin from '@/mixins/filterableMixin'
  import YachtManufacturer from '@/models/YachtManufacturer'
  import YachtManufacturerRow from '@/components/settings/yachtManufacturers/YachtManufacturerRow'
  import YachtManufacturerEditDialog from '@/components/settings/yachtManufacturers/YachtManufacturerEditDialog'
  import TableFooter from '@/components/helpers/TableFooter'

  export default {
    name: 'YachtManufacturersIndex',
    components: {
      YachtManufacturerRow,
      YachtManufacturerEditDialog,
      TableFooter
    },
    mixins: [paginationMixin, filterableMixin],
    data() {
      return {
        yachtManufacturers: {
          data: [],
          loading: false,
          meta: {}
        },
        headers: [
          { text: 'Name', value: 'name', sortable: true },
          { text: 'Country', value: 'country_id', sortable: false }
        ],
        yachtManufacturerDirty: null,
        yachtManufacturerEditDialog: false,
        filtersClean: YachtManufacturer.emptyFilters,
        filtersDirty: cloneDeep(YachtManufacturer.emptyFilters)
      }
    },
    computed: {
      ...mapGetters('settings', ['countries'])
    },
    created() {
      this.fetchYachtManufacturers()
    },
    methods: {
      onPaginationUpdate() {
        this.fetchYachtManufacturers()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      async fetchYachtManufacturers() {
        this.yachtManufacturers.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams
        }

        try {
          let result = await YachtManufacturer.api.fetch(queryParams)

          this.yachtManufacturers.data = result.data
          this.yachtManufacturers.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.yachtManufacturers.loading = false
        }
      },
      openYachtManufacturersEditDialog(yachtManufacturer = YachtManufacturer.emptyModel) {
        if (!this.$can('edit', 'YachtManufacturer')) return

        this.yachtManufacturerDirty = cloneDeep(yachtManufacturer)
        this.yachtManufacturerEditDialog = true
      },
      clearFilters() {
        this.filters = cloneDeep(YachtManufacturer.emptyFilters)
      }
    }
  }
</script>
